import { useState, useEffect } from 'react';
import { User } from 'lucide-react';

interface InfluencerAvatarProps {
  profileImageUrl?: string | null;
  name?: string;
  influencer?: any;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

export default function InfluencerAvatar({
  profileImageUrl,
  name,
  influencer,
  size = 'md',
  className = ''
}: InfluencerAvatarProps) {
  const imageUrl = profileImageUrl || influencer?.profile_picture_url || influencer?.profile_image_url;
  const displayName = name || influencer?.name || influencer?.full_name || 'Influenceur';
  const [imageError, setImageError] = useState(false);
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-16 h-16 text-lg',
  };

  const iconSizes = {
    xs: 12,
    sm: 16,
    md: 20,
    lg: 28,
  };

  useEffect(() => {
    if (!imageUrl) {
      setLoading(false);
      return;
    }

    if (imageUrl.includes('supabase.co/storage')) {
      setImageDataUrl(imageUrl);
      setLoading(false);
    } else {
      setImageDataUrl(imageUrl);
      setLoading(false);
    }
  }, [imageUrl]);

  const getInitials = (fullName: string) => {
    if (!fullName || fullName.trim() === '') return '?';
    const parts = fullName.trim().split(' ').filter(p => p.length > 0);
    if (parts.length === 0) return '?';
    return parts.map(n => n[0]).join('').slice(0, 2).toUpperCase();
  };

  if (!imageUrl || imageError) {
    return (
      <div className={`${sizeClasses[size]} rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white font-medium flex items-center justify-center shadow-sm ${className}`}>
        {displayName ? getInitials(displayName) : <User size={iconSizes[size]} />}
      </div>
    );
  }

  if (loading || !imageDataUrl) {
    return <div className={`${sizeClasses[size]} rounded-full bg-gray-200 animate-pulse ${className}`} />;
  }

  return (
    <img
      src={imageDataUrl}
      alt={displayName}
      className={`${sizeClasses[size]} rounded-full object-cover border-2 border-gray-100 shadow-sm ${className}`}
      onError={() => setImageError(true)}
    />
  );
}
